import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api } from '../../api/client';
import { useAction } from '../../api/hooks';
import type { ActorRef } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Badge, StateBadge, humanize } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Alert, ErrorAlert, QueryView } from '../../ui/feedback';
import { Field, Textarea } from '../../ui/forms';
import { Markdown } from '../../ui/Markdown';
import { ConfirmDialog } from '../../ui/Modal';
import { ActorName, Card, KeyValue, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';

interface Approval {
  id: string;
  displayId: string;
  intent: 'acceptance' | 'release' | 'rollback';
  state: string;
  project: { key: string; name: string; archived: boolean };
  targetRevision: number;
  target: string;
  snapshot: Record<string, unknown>;
  snapshotHash: string;
  current: Record<string, unknown> | null;
  snapshotCurrent: boolean;
  requester: ActorRef;
  origin: string;
  rationale: string;
  discussionLink: string | null;
  outstandingQuestions: string | null;
  createdAt: string;
  expiresAt: string;
  useBy: string | null;
  decidedAt: string | null;
  decidedBy: ActorRef | null;
  decisionReason: string | null;
  staleReason: string | null;
  consumedAt: string | null;
  ticket: { key: string; number: number; status: string } | null;
  release: { id: string; displayId: string; state: string } | null;
  rollback: { id: string; displayId: string; releaseDisplayId: string | null; state: string } | null;
  result: { kind: string; id: string; displayId?: string; number?: number } | null;
  viewer: { canDecide: boolean; canRevoke: boolean; blockedReason: string | null };
}

/**
 * WF-05/06/12/16: one route for every intent. Opening is read-only; an active authenticated admin must
 * explicitly confirm the exact snapshot. A copied link, AI requester or discussion grants nothing.
 */
export function ApprovalDetailPage() {
  const { approvalId } = useParams();
  const q = useQuery({
    queryKey: ['approval', approvalId],
    queryFn: () => api.get<Approval>(`/approvals/${approvalId}`),
  });
  usePageTitle(q.data ? `Approval request ${q.data.displayId}` : null);
  useCrumbs([{ label: 'Approvals', to: '/approvals' }, { label: q.data?.displayId ?? approvalId ?? '' }]);
  const [decision, setDecision] = useState<'approve' | 'deny' | 'revoke' | null>(null);
  const [reason, setReason] = useState('');
  const inv = [['approval', approvalId], ['approvals'], ['me']];
  const decide = useAction(
    (d: 'approve' | 'deny') =>
      api.post(`/approvals/${approvalId}/decision`, {
        decision: d,
        snapshotHash: q.data!.snapshotHash,
        reason: reason || undefined,
      }),
    {
      success: (r) => ((r as { state: string }).state === 'denied' ? 'Request denied' : 'Approved'),
      invalidate: [...inv, ['ticket'], ['board'], ['release']],
      onSuccess: () => setDecision(null),
    },
  );
  const revoke = useAction(() => api.post(`/approvals/${approvalId}/revoke`, { reason }), {
    success: 'Approval revoked',
    invalidate: [...inv, ['release']],
    onSuccess: () => setDecision(null),
  });
  return (
    <div className="page">
      <QueryView query={q}>
        {(a) => (
          <>
            <PageHeader
              title={`Approval request ${a.displayId}`}
              subtitle={`${humanize(a.intent)} · ${a.project.name}`}
              actions={<StateBadge state={a.state} />}
            />
            {a.viewer.blockedReason && a.state === 'pending' && (
              <Alert tone="warning" title="A decision is blocked">
                {a.viewer.blockedReason}
              </Alert>
            )}
            {a.state === 'stale' && (
              <Alert tone="info" title="Stale">
                {a.staleReason ?? 'The target changed after this request.'}
              </Alert>
            )}
            <div className="split">
              <div className="stack">
                {a.intent === 'acceptance' ? (
                  <AcceptanceSnapshot a={a} />
                ) : a.intent === 'release' ? (
                  <ReleaseSnapshot a={a} />
                ) : (
                  <RollbackSnapshot a={a} />
                )}
                {(a.rationale || a.outstandingQuestions || a.discussionLink) && (
                  <Card title="Requester's rationale">
                    {a.rationale && <Markdown>{a.rationale}</Markdown>}
                    {a.outstandingQuestions && (
                      <>
                        <span className="small strong">Outstanding questions</span>
                        <Markdown>{a.outstandingQuestions}</Markdown>
                      </>
                    )}
                    {a.discussionLink && (
                      <span className="small">
                        Discussion:{' '}
                        <a href={a.discussionLink} target="_blank" rel="noopener noreferrer nofollow">
                          {a.discussionLink}
                        </a>
                      </span>
                    )}
                    <span className="small muted">
                      Rationale is information for your review, not authority.
                    </span>
                  </Card>
                )}
              </div>
              <div className="stack">
                <Card title="Decision" tight>
                  <KeyValue
                    items={[
                      [
                        'Requested by',
                        <span key="r">
                          <ActorName actor={a.requester} />{' '}
                          {a.origin === 'mcp' && <Badge dot={false}>via MCP</Badge>}
                        </span>,
                      ],
                      ['Requested', <Timestamp key="c" value={a.createdAt} />],
                      ['Expires', <Timestamp key="e" value={a.expiresAt} />],
                      ['Decided by', a.decidedBy ? <ActorName actor={a.decidedBy} /> : '—'],
                      ['Decided', <Timestamp key="d" value={a.decidedAt} />],
                      ...(a.useBy
                        ? ([['Use by', <Timestamp key="u" value={a.useBy} />]] as [string, React.ReactNode][])
                        : []),
                      ...(a.consumedAt
                        ? ([['Consumed', <Timestamp key="cs" value={a.consumedAt} />]] as [
                            string,
                            React.ReactNode,
                          ][])
                        : []),
                      [
                        'Snapshot',
                        <span key="h" className="mono small">
                          {a.snapshotHash.slice(0, 16)}
                        </span>,
                      ],
                    ]}
                  />
                  {a.decisionReason && <span className="small">“{a.decisionReason}”</span>}
                  {a.viewer.canDecide && (
                    <div className="stack sm">
                      <Button block onClick={() => setDecision('approve')}>
                        {a.intent === 'acceptance'
                          ? 'Accept this scope'
                          : a.intent === 'release'
                            ? 'Approve this release attempt'
                            : 'Approve this rollback attempt'}
                      </Button>
                      <Button block variant="secondary" onClick={() => setDecision('deny')}>
                        Deny request
                      </Button>
                      {a.intent === 'acceptance' && (
                        <span className="small muted">
                          Denying leaves the ticket Pending. To close it, reject the ticket instead.
                        </span>
                      )}
                    </div>
                  )}
                  {a.viewer.canRevoke && a.state === 'approved' && (
                    <Button variant="danger-outline" block onClick={() => setDecision('revoke')}>
                      Revoke before use
                    </Button>
                  )}
                </Card>
                <Card title="Links" tight>
                  <ul className="checklist">
                    {a.ticket && (
                      <li>
                        <Link to={`/projects/${a.project.key}/tickets/${a.ticket.number}`}>
                          {a.ticket.key}
                        </Link>{' '}
                        <span className="muted small">({humanize(a.ticket.status)})</span>
                      </li>
                    )}
                    {a.release && (
                      <li>
                        <Link to={`/projects/${a.project.key}/releases/${a.release.displayId}`}>
                          {a.release.displayId}
                        </Link>{' '}
                        <span className="muted small">({humanize(a.release.state)})</span>
                      </li>
                    )}
                    {a.rollback && a.rollback.releaseDisplayId && (
                      <li>
                        <Link
                          to={`/projects/${a.project.key}/releases/${a.rollback.releaseDisplayId}/rollbacks/${a.rollback.displayId}`}
                        >
                          {a.rollback.displayId}
                        </Link>
                      </li>
                    )}
                    {a.result?.kind === 'acceptance' && (
                      <li>
                        Resulting acceptance <span className="mono">{a.result.displayId}</span>
                      </li>
                    )}
                    {a.result?.kind === 'attempt' && <li>Consumed by attempt ATT-{a.result.number}</li>}
                  </ul>
                </Card>
              </div>
            </div>
            {(decision === 'approve' || decision === 'deny') && (
              <ConfirmDialog
                title={decision === 'approve' ? `Approve ${a.displayId}?` : `Deny ${a.displayId}?`}
                confirmLabel={decision === 'approve' ? 'Yes, approve' : 'Deny request'}
                variant={decision === 'approve' ? 'primary' : 'danger'}
                onClose={() => setDecision(null)}
                onConfirm={() => decide.mutate(decision)}
                pending={decide.isPending}
                error={decide.error}
              >
                <p>
                  {decision === 'approve'
                    ? a.intent === 'acceptance'
                      ? `You confirm scope revision ${a.targetRevision} as reviewed. The ticket moves to Todo in the same step, recorded under your name.`
                      : 'You authorize exactly one attempt for this frozen binding. It must start before the use-by time; a retry needs a new approval.'
                    : 'The request is denied with your name and reason.'}
                </p>
                <Field label="Reason" optional={decision === 'approve'}>
                  {(p) => (
                    <Textarea {...p} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                  )}
                </Field>
              </ConfirmDialog>
            )}
            {decision === 'revoke' && (
              <ConfirmDialog
                title="Revoke this approval?"
                confirmLabel="Yes, revoke"
                variant="danger"
                onClose={() => setDecision(null)}
                onConfirm={() => revoke.mutate(undefined)}
                pending={revoke.isPending}
                error={revoke.error}
                disabled={!reason.trim()}
              >
                <p>
                  The release returns to draft. An attempt that already started keeps its approval as history.
                </p>
                <Field label="Reason">
                  {(p) => (
                    <Textarea {...p} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                  )}
                </Field>
              </ConfirmDialog>
            )}
            <ErrorAlert error={null} />
          </>
        )}
      </QueryView>
    </div>
  );
}

function AcceptanceSnapshot({ a }: { a: Approval }) {
  const s = a.snapshot as {
    ticketKey: string;
    scopeRevision: number;
    type: string;
    title: string;
    description: string;
    acceptanceCriteria: string[];
    stepsOrOutcome: string;
    parent: string | null;
    prerequisites: string[];
  };
  return (
    <Card
      title={`${s.ticketKey} · scope revision ${s.scopeRevision}`}
      subtitle="Exactly what acceptance would authorize."
    >
      <span className="strong">{s.title}</span>
      <Markdown>{s.description}</Markdown>
      {s.stepsOrOutcome && <Markdown>{s.stepsOrOutcome}</Markdown>}
      <span className="small strong">Acceptance criteria</span>
      {s.acceptanceCriteria.length ? (
        <ol style={{ margin: 0, paddingLeft: 20 }}>
          {s.acceptanceCriteria.map((c, i) => (
            <li key={i}>{c}</li>
          ))}
        </ol>
      ) : (
        <Alert tone="error">No acceptance criteria. Acceptance will be refused.</Alert>
      )}
      <KeyValue
        items={[
          ['Type', humanize(s.type)],
          ['Parent', s.parent ?? 'None'],
          ['Prerequisites', s.prerequisites.join(', ') || 'None'],
          [
            'Context',
            <Link key="c" to={`/projects/${a.project.key}/context/overview`}>
              Review project context
            </Link>,
          ],
        ]}
      />
      {!a.snapshotCurrent && a.current && (
        <Alert tone="warning">
          The ticket now differs from this snapshot (current revision{' '}
          {(a.current as { scopeRevision: number }).scopeRevision}).
        </Alert>
      )}
    </Card>
  );
}

interface ReleaseSnap {
  releaseDisplayId: string;
  title: string;
  environmentName: string;
  isCompletionTarget: boolean;
  candidateNumber: number;
  attemptNumber: number;
  tickets: { key: string; title: string; scopeRevision: number; sourceRevision?: string }[];
  repositoryUrl: string | null;
  sourceRevision: string;
  artifactDigest: string | null;
  artifactRef: string | null;
  integrationEvidence: string;
  deploymentPlan: string;
  verificationChecks: string[];
  rollbackPlan: string;
  executor: { kind: string; name: string };
  contextVersions: Record<string, number | null>;
  exceptions: string[];
}

function ReleaseSnapshot({ a }: { a: Approval }) {
  const s = a.snapshot as unknown as ReleaseSnap;
  return (
    <Card
      title={`${s.releaseDisplayId} · candidate ${s.candidateNumber} → ${s.environmentName}`}
      subtitle={`Authorizes attempt ATT-${s.attemptNumber} for this exact binding only.`}
    >
      {s.isCompletionTarget ? (
        <Badge tone="success">Completion environment: success closes included tickets</Badge>
      ) : (
        <Badge>Non-completion environment: tickets stay Pending release</Badge>
      )}
      <KeyValue
        items={[
          [
            'Revision',
            <span key="r" className="mono">
              {s.sourceRevision}
            </span>,
          ],
          [
            'Artifact',
            s.artifactDigest ? (
              <span className="mono small">{s.artifactDigest}</span>
            ) : (
              'None (source revision binding)'
            ),
          ],
          ['Repository', s.repositoryUrl ? <span className="mono small">{s.repositoryUrl}</span> : '—'],
          ['Executor', `${s.executor.name}${s.executor.kind === 'ai_service' ? ' (AI)' : ''}`],
          [
            'Deployment context',
            s.contextVersions.deployment ? `v${s.contextVersions.deployment}` : 'Missing',
          ],
          ['Exceptions', s.exceptions.length ? s.exceptions.join(', ') : 'None'],
        ]}
      />
      <span className="small strong">Tickets and scopes</span>
      <ul className="checklist">
        {s.tickets.map((t) => (
          <li key={t.key}>
            <span className="mono">{t.key}</span> {t.title}{' '}
            <span className="muted small">· scope rev {t.scopeRevision}</span>
          </li>
        ))}
      </ul>
      <span className="small strong">Integration evidence</span>
      <Markdown>{s.integrationEvidence}</Markdown>
      <span className="small strong">Deployment plan</span>
      <Markdown>{s.deploymentPlan}</Markdown>
      <span className="small strong">Required verification checks</span>
      <ul className="checklist">
        {s.verificationChecks.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <span className="small strong">Rollback plan</span>
      <Markdown>{s.rollbackPlan}</Markdown>
    </Card>
  );
}

function RollbackSnapshot({ a }: { a: Approval }) {
  const s = a.snapshot as {
    rollbackDisplayId: string;
    releaseDisplayId: string;
    title: string;
    environmentName: string;
    isCompletionTarget: boolean;
    environmentStateObservation: string;
    rollbackRevision: string;
    artifactDigest: string | null;
    verificationChecks: string[];
    plan: string;
    affectedTickets: { key: string; title: string }[];
    executor: { kind: string; name: string };
    attemptNumber: number;
  };
  return (
    <Card
      title={`${s.rollbackDisplayId} for ${s.releaseDisplayId} → ${s.environmentName}`}
      subtitle={`Authorizes rollback attempt ATT-${s.attemptNumber} only.`}
    >
      <KeyValue
        items={[
          [
            'Restore to',
            <span key="r" className="mono">
              {s.rollbackRevision}
            </span>,
          ],
          ['Artifact', s.artifactDigest ?? 'None'],
          ['Executor', s.executor.name],
        ]}
      />
      <span className="small strong">Environment state as understood</span>
      <Markdown>{s.environmentStateObservation}</Markdown>
      <span className="small strong">Plan</span>
      <Markdown>{s.plan}</Markdown>
      <span className="small strong">Verification checks</span>
      <ul className="checklist">
        {s.verificationChecks.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <span className="small strong">Affected delivered behavior</span>
      {s.isCompletionTarget ? (
        <ul className="checklist">
          {s.affectedTickets.length ? (
            s.affectedTickets.map((t) => (
              <li key={t.key}>
                <span className="mono">{t.key}</span> {t.title} — reopens to Pending on success
              </li>
            ))
          ) : (
            <li className="muted">No tickets reopen</li>
          )}
        </ul>
      ) : (
        <span className="small muted">Non-completion environment: delivered tickets are not reopened.</span>
      )}
    </Card>
  );
}
